import { useQuery } from '@tanstack/react-query';
import {
  getAnalyticsMetrics,
  getTopPages,
  getTrafficSources,
  getDeviceStats,
  getVisitorsByDay,
  type DateRange,
  type AnalyticsMetrics,
  type TopPage,
  type TrafficSource,
  type DeviceStats,
  type VisitorData,
} from '@/lib/analytics/googleAnalyticsData';

/**
 * Hook pour récupérer les métriques principales d'Analytics
 */
export function useAnalyticsMetrics(dateRange: DateRange = '30daysAgo') {
  return useQuery<AnalyticsMetrics>({
    queryKey: ['analytics', 'metrics', dateRange],
    queryFn: () => getAnalyticsMetrics(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook pour récupérer les pages les plus vues
 */
export function useTopPages(dateRange: DateRange = '30daysAgo', limit: number = 10) {
  return useQuery<TopPage[]>({
    queryKey: ['analytics', 'top-pages', dateRange, limit],
    queryFn: () => getTopPages(dateRange, 'today', limit),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer les sources de trafic
 */
export function useTrafficSources(dateRange: DateRange = '30daysAgo') {
  return useQuery<TrafficSource[]>({
    queryKey: ['analytics', 'traffic-sources', dateRange],
    queryFn: () => getTrafficSources(dateRange),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer les statistiques par device
 */
export function useDeviceStats(dateRange: DateRange = '30daysAgo') {
  return useQuery<DeviceStats[]>({
    queryKey: ['analytics', 'device-stats', dateRange],
    queryFn: () => getDeviceStats(dateRange),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer l'évolution des visiteurs jour par jour
 */
export function useVisitorsByDay(dateRange: DateRange = '30daysAgo') {
  return useQuery<VisitorData[]>({
    queryKey: ['analytics', 'visitors-by-day', dateRange],
    queryFn: () => getVisitorsByDay(dateRange),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
